/**
 * The player's saved profile — ONE record in their own atproto repo (collection
 * com.equalnotequals.tanks.profile, rkey "self"). Read unauthenticated (public
 * record); written via the OAuth session. Saves are coalesced + best-effort.
 */

import { type Session, readRecord, writeRecord } from "./atproto";
import { log } from "../engine/debug";

export const PROFILE_NSID = "com.equalnotequals.tanks.profile";

export interface Profile {
  /** volume is 0..1 in memory; stored on the wire as an INTEGER percent (atproto has no floats) */
  settings: { volume: number };
  /** highest layer of the endless tower reached */
  highestLayer: number;
  /** total enemy tanks destroyed across all runs */
  totalKills: number;
  achievements: string[];
  /** chosen cosmetic flair label value ("" = none) */
  flair: string;
  updatedAt: string;
}

export function emptyProfile(): Profile {
  return {
    settings: { volume: 0.5 },
    highestLayer: 0,
    totalKills: 0,
    achievements: [],
    flair: "",
    updatedAt: new Date(0).toISOString(),
  };
}

export class ProfileStore {
  profile: Profile = emptyProfile();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  /** fired ONCE per session if saving fails (HUD shows a "progress not saving" hint) */
  onSaveError: ((message: string) => void) | null = null;
  private errorShown = false;

  constructor(private session: Session) {}

  async load(): Promise<Profile> {
    const rec = await readRecord<Partial<Profile>>(this.session.did, PROFILE_NSID, "self");
    this.profile = { ...emptyProfile(), ...(rec ?? {}) };
    // the wire ALWAYS stores volume as an integer percent (0-100). Read it from
    // the raw record so the in-memory default (a 0..1 fraction) is never
    // mistaken for a percent, and 1% never collides with "100%".
    const wireVol = rec?.settings?.volume;
    this.profile.settings = {
      volume: typeof wireVol === "number" ? Math.max(0, Math.min(1, wireVol / 100)) : 0.5,
    };
    this.profile.achievements = this.profile.achievements ?? [];
    this.profile.flair = typeof this.profile.flair === "string" ? this.profile.flair : "";
    this.profile.highestLayer = this.profile.highestLayer ?? 0;
    this.profile.totalKills = this.profile.totalKills ?? 0;
    return this.profile;
  }

  /** Save now (best-effort — never throws into the game loop). */
  async saveNow(): Promise<void> {
    this.profile.updatedAt = new Date().toISOString();
    try {
      // scale volume to an integer percent — atproto records can't hold floats
      const wire = {
        ...this.profile,
        settings: { volume: Math.round(Math.max(0, Math.min(1, this.profile.settings.volume)) * 100) },
      };
      await writeRecord(this.session, PROFILE_NSID, "self", wire as unknown as Record<string, unknown>);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      log.warn("profile", "save failed", msg);
      if (!this.errorShown) {
        this.errorShown = true;
        // show the REAL error so failures are diagnosable from the screen
        this.onSaveError?.(`progress isn't saving: ${msg.slice(0, 120)}`);
      }
    }
  }

  /** Coalesced save (rapid updates → one write ~1.5s later). */
  save() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.saveNow();
    }, 1500);
  }
}
